import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    Button, Form, Icon, Message, Label, Grid,
    Divider, Popup, Table, Step, Header, Segment, Progress, List
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../app/stores/store';
import CustomDateTimePicker from '../../app/common/form/CustomDateTimePicker';
import CapacitySelector from './CapacitySelector';
import PriceCalculator from './PriceCalculator';
import PaymentStatusSelector from './PaymentStatusSelector';
import {
    RecurrenceType,
    CreateRecurringSeriesDTO,
    RecurringSkippedSlotDTO,
    CreateRecurringSeriesResultDTO,
    CreateRecurringResult,
} from '../../app/models/Calendar';
import { CalendarStatusOption } from './SportAssetCalendar';

type TFunc = ReturnType<typeof useTranslation>['t'];

// ─── Types ────────────────────────────────────────────────────────────────────

interface RecurringSeriesModalProps {
    open:              boolean;
    onClose:           () => void;
    initialAssetId?:   string;
    initialSlotStart?: Date;
    initialSlotEnd?:   Date;
    statusOptions:     CalendarStatusOption[];
}

interface FormState {
    assetId:             string;
    recurrenceType:      RecurrenceType;
    recurrenceDays:      number[];
    startDate:           Date;
    endDate:             Date;
    slotStart:           Date;
    slotEnd:             Date;
    capacityUnits:       number;
    customerFullName:    string;
    customerPhone:       string;
    customerEmail:       string;
    note:                string;
    isPaid:              number;
    statusId:            string;
    manualPriceOverride: number | null;
}

// ─── FIX: Combine step + result into a single atomic state object.
//
// Previously `setSubmitResult(result)` and `setStep(2)` were two separate
// setState calls. In React 17 (and in async contexts even in React 18),
// these are NOT guaranteed to be batched into a single render, so a
// re-render could fire between them with step=0 but a non-null submitResult.
// When MobX then triggers an observer re-render due to the store reload that
// follows series creation, the component could land back on step 0 with the
// form still visible.
//
// Merging them into one object means the transition is always atomic.
interface WizardState {
    step:         0 | 1 | 2;
    submitResult: CreateRecurringResult | null;
}

// ─── Validation ───────────────────────────────────────────────────────────────

interface ValidationErrors {
    assetId?:        string;
    recurrenceDays?: string;
    startDate?:      string;
    endDate?:        string;
    slotTime?:       string;
    capacityUnits?:  string;
    statusId?:       string;
    customerPhone?:  string;
    customerEmail?:  string;
    note?:           string;
}

function validateForm(form: FormState, today: Date, maxEndDate: Date, t: TFunc): ValidationErrors {
    const errors: ValidationErrors = {};

    if (!form.assetId)
        errors.assetId = t('recurring.validation.assetRequired');

    if (form.recurrenceDays.length === 0)
        errors.recurrenceDays = t('recurring.validation.daysRequired');

    if (form.startDate < today)
        errors.startDate = t('recurring.validation.startDateFuture');

    if (form.endDate <= form.startDate)
        errors.endDate = t('recurring.validation.endDateAfterStart');
    else if (form.endDate > maxEndDate)
        errors.endDate = t('recurring.validation.endDateMaxOneYear');

    if (form.slotStart >= form.slotEnd)
        errors.slotTime = t('recurring.validation.slotEndAfterStart');

    if (!form.capacityUnits || form.capacityUnits <= 0)
        errors.capacityUnits = t('recurring.validation.capacityRequired');

    if (!form.statusId)
        errors.statusId = t('recurring.validation.statusRequired');

    if (!form.customerPhone || !form.customerPhone.trim())
        errors.customerPhone = t('recurring.validation.phoneRequired');

    if (!form.customerEmail || !form.customerEmail.trim())
        errors.customerEmail = t('recurring.validation.emailRequired');

    if (!form.note || !form.note.trim())
        errors.note = t('recurring.validation.noteRequired');

    return errors;
}

const DAY_LABELS: Record<number, string> = {
    0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dateToDateOnlyString(d: Date): string {
    return d.toLocaleDateString('en-CA');
}

function parseDateLocal(s: string): Date {
    const [y, mo, d] = s.split('-').map(Number);
    const dt = new Date();
    dt.setFullYear(y, mo - 1, d);
    dt.setHours(0, 0, 0, 0);
    return dt;
}

function todayMidnight(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function oneYearFromDate(d: Date): Date {
    const r = new Date(d);
    r.setFullYear(r.getFullYear() + 1);
    return r;
}

function previewOccurrences(
    recurrenceType: RecurrenceType, recurrenceDays: number[],
    startDate: Date, endDate: Date, slotStart: Date, slotEnd: Date, limit = 365,
): Array<{ start: Date; end: Date }> {
    const results: Array<{ start: Date; end: Date }> = [];
    const targetDays = new Set(recurrenceDays);
    const cursor = new Date(startDate);
    cursor.setHours(slotStart.getHours(), slotStart.getMinutes(), 0, 0);
    const limitDate = new Date(endDate);
    limitDate.setHours(23, 59, 59, 999);
    const makeEnd = (s: Date) => {
        const e = new Date(s);
        e.setHours(slotEnd.getHours(), slotEnd.getMinutes(), 0, 0);
        return e;
    };
    if (recurrenceType === 'WEEKLY') {
        while (cursor <= limitDate && !targetDays.has(cursor.getDay()))
            cursor.setDate(cursor.getDate() + 1);
        while (cursor <= limitDate && results.length < limit) {
            const s = new Date(cursor);
            results.push({ start: s, end: makeEnd(s) });
            cursor.setDate(cursor.getDate() + 7);
        }
    } else {
        while (cursor <= limitDate && results.length < limit) {
            if (targetDays.has(cursor.getDay())) {
                const s = new Date(cursor);
                results.push({ start: s, end: makeEnd(s) });
            }
            cursor.setDate(cursor.getDate() + 1);
        }
    }
    return results;
}


// ─── Component ────────────────────────────────────────────────────────────────

const RecurringSeriesModal: React.FC<RecurringSeriesModalProps> = observer(({
    open, onClose, initialAssetId, initialSlotStart, initialSlotEnd, statusOptions,
}) => {
    const { t } = useTranslation();
    const { calendarStore } = useStore();
    const resources = calendarStore.resources;

    // FIX: Single atomic state for wizard position + result.
    // A single setState call = a single render = no intermediate state visible
    // to MobX observer re-renders triggered by the post-creation store reload.
    const [wizard, setWizard] = useState<WizardState>({ step: 0, submitResult: null });

    const [showValidationErrors, setShowValidationErrors] = useState(false);

    // FIX: A ref-based guard that is set to true the moment we have a successful
    // result. MobX observer re-renders check this ref (not state) so they cannot
    // overwrite the success screen even if the store emits multiple updates while
    // the network round-trip is settling on prod/Docker.
    const successLockedRef = useRef(false);

    // ROOT CAUSE FIX: `today` and `oneYear` were computed inline on every render,
    // giving them a new object identity each time. Because `buildDefault` closed
    // over them, `buildDefault` also got a new identity on every re-render.
    // The reset useEffect had `buildDefault` as a dependency, so whenever MobX
    // triggered a re-render (e.g. when `calendarStore.resources` first populated
    // after the very first recurring series creation), the effect re-ran, called
    // `successLockedRef.current = false` and `setWizard({ step: 0 })`, wiping the
    // success screen. This only happened on the FIRST creation because that was the
    // only time `resources` went from empty → populated, changing `buildDefault`.
    //
    // Fix: freeze `today` and `oneYear` into refs on mount so they are the same
    // object reference for the component's entire lifetime. This makes `buildDefault`
    // stable, which in turn makes the reset effect stable — it will ONLY re-run when
    // `open` genuinely changes, never due to a MobX store update.
    const todayRef   = useRef(todayMidnight());
    const oneYearRef = useRef(oneYearFromDate(todayRef.current));

    const buildDefault = useCallback((): FormState => {
        const slotStart = initialSlotStart ?? (() => { const d = new Date(); d.setHours(10, 0, 0, 0); return d; })();
        const slotEnd   = initialSlotEnd   ?? (() => { const d = new Date(); d.setHours(11, 0, 0, 0); return d; })();
        const durationHours = (slotEnd.getTime() - slotStart.getTime()) / (1000 * 60 * 60);

        return {
            assetId: initialAssetId ?? '',
            recurrenceType: 'WEEKLY',
            recurrenceDays: [],
            startDate: todayRef.current,
            endDate: oneYearRef.current,
            slotStart,
            slotEnd,
            capacityUnits: 0,
            customerFullName: '',
            customerPhone: '',
            customerEmail: '',
            note: t('calendars.defaultBookingNote', { duration: durationHours }),
            isPaid: 0,
            statusId: '',
            manualPriceOverride: null,
        };
    // todayRef and oneYearRef are stable refs — intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialAssetId, initialSlotStart, initialSlotEnd]);

    const [form, setForm] = useState<FormState>(buildDefault);

    // Reset effect — keyed ONLY on `open`.
    // `buildDefault` is listed for correctness but is now truly stable (its only
    // changing deps are the initial* props which don't change during a session).
    // Critically: calendarStore is NOT here, so MobX store updates (resources
    // loading, events reloading after creation) can never trigger this effect.
    useEffect(() => {
        if (open) {
            successLockedRef.current = false;
            setWizard({ step: 0, submitResult: null });
            setShowValidationErrors(false);
            setForm(buildDefault());
        }
    }, [open, buildDefault]); // eslint-disable-line react-hooks/exhaustive-deps

    // Separate effect: clear price calculation state. Intentionally isolated
    // so calendarStore changes never bleed into the wizard reset above.
    useEffect(() => {
        calendarStore.clearPriceCalculation();
    }, [open, calendarStore]);

    // Keep note in sync when slot times change
    const prevSlotRef = React.useRef({ start: form.slotStart, end: form.slotEnd });
    useEffect(() => {
        const prev = prevSlotRef.current;
        const prevDuration = (prev.end.getTime() - prev.start.getTime()) / (1000 * 60 * 60);
        const newDuration  = (form.slotEnd.getTime() - form.slotStart.getTime()) / (1000 * 60 * 60);
        const prevDefault  = t('calendars.defaultBookingNote', { duration: prevDuration });
        prevSlotRef.current = { start: form.slotStart, end: form.slotEnd };
        if (form.note === prevDefault) {
            setForm(p => ({ ...p, note: t('calendars.defaultBookingNote', { duration: newDuration }) }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.slotStart, form.slotEnd]);

    const selectedResource = useMemo(
        () => resources.find(r => r.id === form.assetId) ?? null,
        [resources, form.assetId],
    );

    const previews = useMemo(() => {
        if (!form.recurrenceDays.length || !form.assetId) return [];
        return previewOccurrences(
            form.recurrenceType, form.recurrenceDays,
            form.startDate, form.endDate, form.slotStart, form.slotEnd,
        );
    }, [form.recurrenceType, form.recurrenceDays,
        form.startDate, form.endDate, form.slotStart, form.slotEnd, form.assetId]);

    const maxEndDate = useMemo(() => oneYearFromDate(form.startDate), [form.startDate]);

    // Stable today for validation (uses frozen ref, not a new Date() each render)
    const today = todayRef.current;

    const showPriceCalc = !!(
        form.assetId && form.capacityUnits > 0 &&
        form.slotStart && form.slotEnd && form.slotStart < form.slotEnd
    );

    // BUG FIX: Previously used normalizeToTodayWithTime() which replaced the DATE
    // with today, keeping only the time-of-day from form.slotStart/slotEnd.
    // This caused day-of-week discount rules (e.g. "Happy Hour on Fridays") to
    // never match when creating a recurring series — if today is not Friday, the
    // price API would evaluate against today's date and find no applicable discount,
    // even though all the actual slots will occur on Fridays.
    //
    // Fix: use the first preview occurrence's real date combined with the slot time.
    // This is the most representative slot and carries the correct day-of-week so
    // all time/day-based discount rules evaluate accurately.
    // If no previews exist yet (days not selected), fall back to today as before.
    const priceStart = useMemo(() => {
        const baseDate = previews.length > 0 ? previews[0].start : new Date();
        const d = new Date(baseDate);
        d.setHours(form.slotStart.getHours(), form.slotStart.getMinutes(), 0, 0);
        return d;
    }, [previews, form.slotStart]);

    const priceEnd = useMemo(() => {
        const baseDate = previews.length > 0 ? previews[0].start : new Date();
        const d = new Date(baseDate);
        d.setHours(form.slotEnd.getHours(), form.slotEnd.getMinutes(), 0, 0);
        return d;
    }, [previews, form.slotEnd]);

    const onPriceOverrideChange = useCallback(
        (v: number | null) => setForm(p => ({ ...p, manualPriceOverride: v })),
        [],
    );

    const validationErrors = useMemo(
        () => validateForm(form, today, maxEndDate, t),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [form, maxEndDate],
    );

    const hasErrors = Object.keys(validationErrors).length > 0;

    const configValid = !hasErrors && previews.length > 0;

    const toggleDay = useCallback((day: number) => {
        setForm(prev => {
            const isWeekly = prev.recurrenceType === 'WEEKLY';
            const selected = prev.recurrenceDays.includes(day);
            if (isWeekly) return { ...prev, recurrenceDays: selected ? [] : [day] };
            const days = selected
                ? prev.recurrenceDays.filter(d => d !== day)
                : [...prev.recurrenceDays, day].sort((a, b) => a - b);
            return { ...prev, recurrenceDays: days };
        });
    }, []);

    const switchRecurrenceType = useCallback((rt: RecurrenceType) => {
        setForm(prev => ({ ...prev, recurrenceType: rt, recurrenceDays: [] }));
    }, []);

    const handleTryProceed = useCallback(() => {
        if (hasErrors || previews.length === 0) {
            setShowValidationErrors(true);
            return;
        }
        setShowValidationErrors(false);
        setWizard({ step: 1, submitResult: null });
    }, [hasErrors, previews.length]);

    // Stable ref to avoid recreating callbacks when store updates post-creation
    const storeRef = useRef(calendarStore);
    useEffect(() => { storeRef.current = calendarStore; }, [calendarStore]);

    const handleSubmit = useCallback(async () => {
        const dto: CreateRecurringSeriesDTO = {
            companySportVenueAssetId: form.assetId,
            recurrenceType:           form.recurrenceType,
            recurrenceDays:           form.recurrenceDays,
            startDate:                dateToDateOnlyString(form.startDate),
            endDate:                  dateToDateOnlyString(form.endDate),
            slotStart:                form.slotStart.toISOString(),
            slotEnd:                  form.slotEnd.toISOString(),
            capacityUnitsRequested:   form.capacityUnits,
            customerFullName:         form.customerFullName || undefined,
            customerPhone:            form.customerPhone    || undefined,
            customerEmail:            form.customerEmail    || undefined,
            note:                     form.note,
            isPaid:                   form.isPaid,
            statusId:                 form.statusId         || undefined,
            manualPriceOverride:      form.manualPriceOverride,
        };

        const result = await storeRef.current.createRecurringSeries(dto);

        // FIX: Lock the success screen BEFORE setting state so any MobX
        // observer re-render that fires synchronously during setState (due
        // to store updates on the same tick) sees the lock and cannot
        // override the wizard position.
        successLockedRef.current = true;

        // FIX: Single atomic setState — step and result change in one render.
        // This eliminates the race window between two separate setX() calls.
        setWizard({ step: 2, submitResult: result });
    }, [form]); // calendarStore accessed via storeRef — intentionally excluded

    const handleClose = useCallback(() => {
        successLockedRef.current = false;
        setWizard({ step: 0, submitResult: null });
        setShowValidationErrors(false);
        storeRef.current.clearPriceCalculation();
        onClose();
    }, [onClose]);

    // FIX: Guard against MobX-triggered re-renders resetting the wizard while
    // the user is on the success screen. If the lock ref is set, we always
    // render step 2 regardless of what `wizard.step` says at that moment.
    const effectiveStep = successLockedRef.current ? 2 : wizard.step;
    const submitResult  = wizard.submitResult;

    const isMobile = window.innerWidth <= 768;

    const fieldError = (key: keyof ValidationErrors) =>
        showValidationErrors && validationErrors[key]
            ? validationErrors[key]
            : undefined;

    const errorInputStyle = (key: keyof ValidationErrors): React.CSSProperties => ({
        ...inputStyle,
        borderColor: fieldError(key) ? '#dc3545' : '#ced4da',
    });

    if (!open) return null;

    return (
        <div
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
                display: 'flex', justifyContent: 'center',
                alignItems: isMobile ? 'flex-end' : 'center',
                padding: isMobile ? 0 : '15px', overflowY: 'auto',
            }}
            onClick={handleClose}
        >
        <div
            style={{
                backgroundColor: 'white',
                borderRadius: isMobile ? '16px 16px 0 0' : '12px',
                width: '100%', maxWidth: isMobile ? '100%' : '860px',
                maxHeight: isMobile ? '95vh' : '92vh',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
        >
            {/* ── Header ── */}
            <div style={{
                background: 'linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)',
                color: 'white', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '0.75rem',
                padding: isMobile ? '0.9rem 1rem' : '1.1rem 1.5rem',
                flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Icon name="repeat" size="large" />
                    <span style={{ fontSize: isMobile ? '1rem' : '1.15rem', fontWeight: 700 }}>
                        {t('recurring.modal.title')}
                    </span>
                </div>
                <Button circular icon="close" size="small" inverted
                        onClick={handleClose} style={{ margin: 0, flexShrink: 0 }} />
            </div>

            {/* ── Step bar ── */}
            <div style={{ padding: '0.6rem 1rem 0', background: '#f8f9fa', borderBottom: '1px solid #dee2e6', flexShrink: 0 }}>
                <Step.Group fluid size="mini">
                    <Step active={effectiveStep === 0} completed={effectiveStep > 0}>
                        <Icon name="settings" />
                        <Step.Content><Step.Title>{t('recurring.steps.configure')}</Step.Title></Step.Content>
                    </Step>
                    <Step active={effectiveStep === 1} completed={effectiveStep > 1} disabled={effectiveStep < 1}>
                        <Icon name="eye" />
                        <Step.Content><Step.Title>{t('recurring.steps.preview')}</Step.Title></Step.Content>
                    </Step>
                    <Step active={effectiveStep === 2} disabled={effectiveStep < 2}>
                        <Icon name="check circle" />
                        <Step.Content><Step.Title>{t('recurring.steps.done')}</Step.Title></Step.Content>
                    </Step>
                </Step.Group>
            </div>

            <div style={{
                padding: isMobile ? '1rem' : '1.5rem',
                overflowY: 'auto', flex: 1,
            }}>

                {/* ══ STEP 0 – CONFIGURE ══ */}
                {effectiveStep === 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

                        {showValidationErrors && hasErrors && (
                            <Message negative icon size="small">
                                <Icon name="exclamation triangle" />
                                <Message.Content>
                                    <Message.Header>{t('recurring.validation.summaryTitle')}</Message.Header>
                                    <List bulleted style={{ marginTop: '0.4rem' }}>
                                        {Object.values(validationErrors).map((err, i) => (
                                            <List.Item key={i}>{err}</List.Item>
                                        ))}
                                    </List>
                                </Message.Content>
                            </Message>
                        )}

                        {/* Asset */}
                        <Form.Field error={!!fieldError('assetId')}>
                            <label style={labelStyle}>{t('calendars.modal.asset')} *</label>
                            <select
                                value={form.assetId}
                                onChange={e => {
                                    setForm(p => ({ ...p, assetId: e.target.value, capacityUnits: 0, manualPriceOverride: null }));
                                    calendarStore.clearPriceCalculation();
                                }}
                                style={{ ...selectStyle, borderColor: fieldError('assetId') ? '#dc3545' : '#ced4da' }}
                            >
                                <option value="">{t('calendars.modal.selectAsset')}</option>
                                {resources.filter(r => r.isActive && r.isBookable).map(r => (
                                    <option key={r.id} value={r.id}>
                                        {r.name} – {r.venueName} ({r.totalCapacityUnits} {t('capacity.units')})
                                    </option>
                                ))}
                            </select>
                            {fieldError('assetId') && (
                                <div style={errorTextStyle}><Icon name="exclamation circle" />{fieldError('assetId')}</div>
                            )}
                        </Form.Field>

                        {/* Pattern */}
                        <Form.Field>
                            <label style={labelStyle}>{t('recurring.form.recurrenceType')} *</label>
                            <Button.Group>
                                {(['WEEKLY', 'SPECIFIC_DAYS'] as RecurrenceType[]).map(rt => (
                                    <Button key={rt}
                                            color={form.recurrenceType === rt ? 'blue' : undefined}
                                            basic={form.recurrenceType !== rt}
                                            onClick={() => switchRecurrenceType(rt)}
                                            content={t(`recurring.types.${rt}`)} />
                                ))}
                            </Button.Group>
                            <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: '#888' }}>
                                {form.recurrenceType === 'WEEKLY'
                                    ? t('recurring.form.weeklyHint')
                                    : t('recurring.form.specificDaysHint')}
                            </div>
                        </Form.Field>

                        {/* Day picker */}
                        <Form.Field error={!!fieldError('recurrenceDays')}>
                            <label style={labelStyle}>
                                {form.recurrenceType === 'WEEKLY'
                                    ? t('recurring.form.selectDay')
                                    : t('recurring.form.selectDays')} *
                            </label>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                {[0, 1, 2, 3, 4, 5, 6].map(day => {
                                    const selected = form.recurrenceDays.includes(day);
                                    const disabled =
                                        form.recurrenceType === 'WEEKLY' &&
                                        form.recurrenceDays.length > 0 &&
                                        !selected;
                                    return (
                                        <Button key={day} circular size="small"
                                                color={selected ? 'blue' : undefined}
                                                basic={!selected} disabled={disabled}
                                                onClick={() => toggleDay(day)}
                                                style={{ minWidth: isMobile ? '2.6rem' : '3rem' }}>
                                            {t(`recurring.days.${DAY_LABELS[day]}`)}
                                        </Button>
                                    );
                                })}
                            </div>
                            {fieldError('recurrenceDays') && (
                                <div style={errorTextStyle}><Icon name="exclamation circle" />{fieldError('recurrenceDays')}</div>
                            )}
                        </Form.Field>

                        {/* Date range */}
                        <Grid stackable columns={2}>
                            <Grid.Column>
                                <Form.Field error={!!fieldError('startDate')}>
                                    <label style={labelStyle}>{t('recurring.form.seriesStart')} *</label>
                                    <input type="date"
                                           value={form.startDate.toLocaleDateString('en-CA')}
                                           min={todayRef.current.toLocaleDateString('en-CA')}
                                           max={oneYearRef.current.toLocaleDateString('en-CA')}
                                           onChange={e => {
                                               if (!e.target.value) return;
                                               const nd = parseDateLocal(e.target.value);
                                               setForm(p => ({
                                                   ...p, startDate: nd,
                                                   endDate: p.endDate <= nd ? oneYearFromDate(nd) : p.endDate,
                                               }));
                                           }}
                                           style={{ ...inputStyle, borderColor: fieldError('startDate') ? '#dc3545' : '#ced4da' }} />
                                    {fieldError('startDate') && (
                                        <div style={errorTextStyle}><Icon name="exclamation circle" />{fieldError('startDate')}</div>
                                    )}
                                </Form.Field>
                            </Grid.Column>
                            <Grid.Column>
                                <Form.Field error={!!fieldError('endDate')}>
                                    <label style={labelStyle}>
                                        {t('recurring.form.seriesEnd')} *
                                        <Popup
                                            trigger={<Icon name="info circle" style={{ marginLeft: '0.4rem', color: '#888' }} />}
                                            content={t('recurring.form.seriesEndHint')} size="tiny"
                                        />
                                    </label>
                                    <input type="date"
                                           value={form.endDate.toLocaleDateString('en-CA')}
                                           min={new Date(form.startDate.getTime() + 86400000).toLocaleDateString('en-CA')}
                                           max={maxEndDate.toLocaleDateString('en-CA')}
                                           onChange={e => {
                                               if (!e.target.value) return;
                                               const nd = parseDateLocal(e.target.value);
                                               nd.setHours(23, 59, 59, 0);
                                               setForm(p => ({ ...p, endDate: nd }));
                                           }}
                                           style={{ ...inputStyle, borderColor: fieldError('endDate') ? '#dc3545' : '#ced4da' }} />
                                    {fieldError('endDate') && (
                                        <div style={errorTextStyle}><Icon name="exclamation circle" />{fieldError('endDate')}</div>
                                    )}
                                </Form.Field>
                            </Grid.Column>
                        </Grid>

                        {/* Slot times */}
                        <Grid stackable columns={2}>
                            <Grid.Column>
                                <CustomDateTimePicker
                                    label={`${t('recurring.form.slotStart')} *`}
                                    value={form.slotStart}
                                    onChange={d => {
                                        setForm(p => ({ ...p, slotStart: d ?? p.slotStart, manualPriceOverride: null }));
                                        calendarStore.clearPriceCalculation();
                                    }}
                                />
                            </Grid.Column>
                            <Grid.Column>
                                <CustomDateTimePicker
                                    label={`${t('recurring.form.slotEnd')} *`}
                                    value={form.slotEnd}
                                    onChange={d => {
                                        setForm(p => ({ ...p, slotEnd: d ?? p.slotEnd, manualPriceOverride: null }));
                                        calendarStore.clearPriceCalculation();
                                    }}
                                    isEndTime={true}
                                    startDateTime={form.slotStart}
                                    minGapMinutes={30}
                                />
                            </Grid.Column>
                        </Grid>
                        {fieldError('slotTime') && (
                            <div style={errorTextStyle}><Icon name="exclamation circle" />{fieldError('slotTime')}</div>
                        )}

                        {/* Capacity */}
                        <div style={{ borderColor: fieldError('capacityUnits') ? '#dc3545' : undefined, borderWidth: fieldError('capacityUnits') ? 1 : 0, borderStyle: 'solid', borderRadius: 6 }}>
                            <CapacitySelector
                                resource={selectedResource}
                                selectedCapacity={form.capacityUnits}
                                onCapacityChange={v => {
                                    setForm(p => ({ ...p, capacityUnits: v, manualPriceOverride: null }));
                                    calendarStore.clearPriceCalculation();
                                }}
                                required
                            />
                        </div>
                        {fieldError('capacityUnits') && (
                            <div style={errorTextStyle}><Icon name="exclamation circle" />{fieldError('capacityUnits')}</div>
                        )}

                        {showPriceCalc && (
                            <PriceCalculator
                                assetId={form.assetId}
                                dateTimeStart={priceStart}
                                dateTimeEnd={priceEnd}
                                capacityUnitsRequested={form.capacityUnits}
                                autoCalculate={true}
                                manualPriceOverride={form.manualPriceOverride}
                                onManualPriceOverrideChange={onPriceOverrideChange}
                            />
                        )}

                        <Divider horizontal>
                            <span style={{ color: '#888', fontSize: '0.82rem' }}>
                                {t('recurring.form.bookingDetails')}
                            </span>
                        </Divider>

                        {/* Status */}
                        <Form.Field error={!!fieldError('statusId')}>
                            <label style={labelStyle}>{t('calendars.modal.status')} *</label>
                            <select value={form.statusId}
                                    onChange={e => setForm(p => ({ ...p, statusId: e.target.value }))}
                                    style={{ ...selectStyle, borderColor: fieldError('statusId') ? '#dc3545' : '#ced4da' }}>
                                <option value="">{t('calendars.modal.selectStatus')}</option>
                                {statusOptions.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                            {fieldError('statusId') && (
                                <div style={errorTextStyle}><Icon name="exclamation circle" />{fieldError('statusId')}</div>
                            )}
                        </Form.Field>

                        {/* Customer fields */}
                        <Grid stackable columns={isMobile ? 1 : 3}>
                            <Grid.Column>
                                <Form.Field>
                                    <label style={labelStyle}>{t('calendars.modal.customerFullName')}</label>
                                    <input type="text" value={form.customerFullName}
                                           onChange={e => setForm(p => ({ ...p, customerFullName: e.target.value }))}
                                           placeholder={t('calendars.modal.placeholders.fullName')}
                                           style={inputStyle} />
                                </Form.Field>
                            </Grid.Column>
                            <Grid.Column>
                                <Form.Field error={!!fieldError('customerPhone')}>
                                    <label style={labelStyle}>{t('calendars.modal.customerPhone')} *</label>
                                    <input type="tel" value={form.customerPhone}
                                           onChange={e => setForm(p => ({ ...p, customerPhone: e.target.value }))}
                                           placeholder={t('calendars.modal.placeholders.phone')}
                                           style={errorInputStyle('customerPhone')} />
                                    {fieldError('customerPhone') && (
                                        <div style={errorTextStyle}><Icon name="exclamation circle" />{fieldError('customerPhone')}</div>
                                    )}
                                </Form.Field>
                            </Grid.Column>
                            <Grid.Column>
                                <Form.Field error={!!fieldError('customerEmail')}>
                                    <label style={labelStyle}>{t('calendars.modal.customerEmail')} *</label>
                                    <input type="email" value={form.customerEmail}
                                           onChange={e => setForm(p => ({ ...p, customerEmail: e.target.value }))}
                                           placeholder={t('calendars.modal.placeholders.email')}
                                           style={errorInputStyle('customerEmail')} />
                                    {fieldError('customerEmail') && (
                                        <div style={errorTextStyle}><Icon name="exclamation circle" />{fieldError('customerEmail')}</div>
                                    )}
                                </Form.Field>
                            </Grid.Column>
                        </Grid>

                        {(!form.customerPhone.trim() || !form.customerEmail.trim()) && (
                            <Message warning size="tiny" style={{ marginTop: '-0.25rem' }}>
                                <Icon name="exclamation triangle" />
                                {t('calendars.modal.phoneEmailRequired')}
                            </Message>
                        )}

                        {/* Note */}
                        <Form.Field error={!!fieldError('note')}>
                            <label style={labelStyle}>{t('calendars.modal.note')} *</label>
                            <textarea value={form.note} rows={isMobile ? 3 : 2}
                                      onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                                      placeholder={t('calendars.modal.placeholders.notes')}
                                      style={{
                                          ...inputStyle,
                                          resize: 'vertical',
                                          fontFamily: 'inherit',
                                          borderColor: form.note.trim() ? '#ced4da' : '#dc3545',
                                      }} />
                            {!form.note.trim() && (
                                <Message warning size="tiny" style={{ marginTop: '0.25rem' }}>
                                    <Icon name="exclamation triangle" />
                                    {t('calendars.modal.noteRequired')}
                                </Message>
                            )}
                            {fieldError('note') && (
                                <div style={errorTextStyle}><Icon name="exclamation circle" />{fieldError('note')}</div>
                            )}
                        </Form.Field>

                        {/* Payment */}
                        <Form.Field>
                            <label style={labelStyle}>{t('calendars.modal.paymentStatus')}</label>
                            <PaymentStatusSelector value={form.isPaid}
                                                   onChange={v => setForm(p => ({ ...p, isPaid: v }))} fluid />
                        </Form.Field>

                        {previews.length > 0 && !hasErrors && (
                            <Message info size="tiny" icon>
                                <Icon name="calendar check outline" />
                                <Message.Content>
                                    {t('recurring.form.previewHint', { count: previews.length })}
                                </Message.Content>
                            </Message>
                        )}
                    </div>
                )}

                {/* ══ STEP 1 – PREVIEW ══ */}
                {effectiveStep === 1 && (
                    <div>
                        <Message info icon size="small" style={{ marginBottom: '1rem' }}>
                            <Icon name="info circle" />
                            <Message.Content>
                                <p>{t('recurring.preview.allOrNothingWarning')}</p>
                                <strong>
                                    {t('recurring.preview.readyToCreate', { count: previews.length })}
                                </strong>
                            </Message.Content>
                        </Message>
                        <div style={{
                            maxHeight: '380px', overflowY: 'auto',
                            border: '1px solid #dee2e6', borderRadius: '6px',
                        }}>
                            <Table celled compact size="small" style={{ margin: 0 }}>
                                <Table.Header style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8f9fa' }}>
                                    <Table.Row>
                                        <Table.HeaderCell style={{ width: '2.5rem' }}>#</Table.HeaderCell>
                                        <Table.HeaderCell>{t('recurring.preview.date')}</Table.HeaderCell>
                                        <Table.HeaderCell>{t('recurring.preview.day')}</Table.HeaderCell>
                                        <Table.HeaderCell>{t('recurring.preview.time')}</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {previews.map((occ, i) => (
                                        <Table.Row key={i}>
                                            <Table.Cell style={{ color: '#aaa' }}>{i + 1}</Table.Cell>
                                            <Table.Cell>
                                                {occ.start.toLocaleDateString(undefined, {
                                                    day: 'numeric', month: 'short', year: 'numeric',
                                                })}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {occ.start.toLocaleDateString(undefined, { weekday: 'long' })}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {form.slotStart.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                                {' – '}
                                                {form.slotEnd.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </div>
                    </div>
                )}

                {/* ══ STEP 2 – RESULT ══ */}
                {effectiveStep === 2 && submitResult && (
                    <ResultPanel result={submitResult} t={t} />
                )}
            </div>

            {/* ── Footer ── */}
            <div style={{
                background: '#f8f9fa',
                padding: isMobile ? '0.75rem' : '0.75rem 1.25rem',
                display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem',
                flexShrink: 0, borderTop: '1px solid #dee2e6',
            }}>
                {effectiveStep === 0 && (
                    <>
                        <Button onClick={handleClose} icon="times" content={t('calendars.modal.cancel')} />
                        <Button primary
                                onClick={handleTryProceed}
                                icon="arrow right" labelPosition="right"
                                content={previews.length > 0 && !hasErrors
                                    ? t('recurring.actions.preview', { count: previews.length })
                                    : t('recurring.actions.previewCheck')} />
                    </>
                )}
                {effectiveStep === 1 && (
                    <>
                        <Button onClick={() => setWizard({ step: 0, submitResult: null })} icon="arrow left"
                                content={t('recurring.actions.back')} />
                        <Button positive loading={calendarStore.loadingRecurring}
                                disabled={calendarStore.loadingRecurring}
                                onClick={handleSubmit} icon="calendar check" labelPosition="right"
                                content={t('recurring.actions.createAll', { count: previews.length })} />
                    </>
                )}
                {effectiveStep === 2 && (
                    <Button positive fluid onClick={handleClose} icon="check"
                            content={t('common.close')} />
                )}
            </div>
        </div>
        </div>
    );
});

export default RecurringSeriesModal;

// ─── Result Panel ─────────────────────────────────────────────────────────────

interface ResultPanelProps { result: CreateRecurringResult; t: TFunc; }

const ResultPanel: React.FC<ResultPanelProps> = ({ result, t }) => {
    if (result.type === 'error') {
        return (
            <Message negative icon>
                <Icon name="times circle" />
                <Message.Content>
                    <Message.Header>{t('recurring.result.errorTitle')}</Message.Header>
                    <p>{result.message}</p>
                </Message.Content>
            </Message>
        );
    }

    const data: CreateRecurringSeriesResultDTO = result.data;

    if (result.type === 'nothing' && data.skippedSlots?.length > 0) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Message negative icon>
                    <Icon name="times circle" size="big" />
                    <Message.Content>
                        <Message.Header>{t('recurring.result.conflictBlockedTitle')}</Message.Header>
                        <p>{t('recurring.result.conflictBlockedBody', {
                            count: data.skippedSlots.length,
                            total: data.slotsPlanned,
                        })}</p>
                    </Message.Content>
                </Message>
                <Header as="h4" style={{ margin: 0 }}>
                    <Icon name="exclamation triangle" color="red" />
                    {t('recurring.result.conflictListTitle', { count: data.skippedSlots.length })}
                </Header>
                <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid #dc3545', borderRadius: '6px' }}>
                    <Table celled compact size="small" style={{ margin: 0 }}>
                        <Table.Header style={{ position: 'sticky', top: 0, background: '#fff5f5', zIndex: 1 }}>
                            <Table.Row>
                                <Table.HeaderCell>{t('recurring.conflict.date')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('recurring.conflict.used')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('recurring.conflict.available')}</Table.HeaderCell>
                                <Table.HeaderCell>{t('recurring.conflict.requested')}</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {data.skippedSlots.map((s: RecurringSkippedSlotDTO, i: number) => (
                                <Table.Row key={i} negative>
                                    <Table.Cell>
                                        <strong>
                                            {new Date(s.occurrenceDate).toLocaleDateString(undefined, {
                                                weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                                            })}
                                        </strong>
                                    </Table.Cell>
                                    <Table.Cell>{s.usedCapacity} / {s.totalCapacity}</Table.Cell>
                                    <Table.Cell>
                                        <Label size="tiny" color="red">{s.availableCapacity}</Label>
                                    </Table.Cell>
                                    <Table.Cell>{s.requestedCapacity}</Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </div>
                <Message info size="small">
                    <Icon name="lightbulb" />
                    {t('recurring.result.conflictSuggestion')}
                </Message>
            </div>
        );
    }

    if (result.type === 'nothing') {
        return (
            <Message negative icon>
                <Icon name="times circle" />
                <Message.Content>
                    <Message.Header>{t('recurring.result.nothingTitle')}</Message.Header>
                    <p>{t('recurring.result.nothingBody', { count: data.slotsPlanned })}</p>
                </Message.Content>
            </Message>
        );
    }

    const someSkipped = result.type === 'partial';
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Segment color={someSkipped ? 'orange' : 'green'} textAlign="center">
                <Icon name={someSkipped ? 'exclamation triangle' : 'check circle'}
                      size="huge" color={someSkipped ? 'orange' : 'green'} />
                <Header as="h3" style={{ marginTop: '0.5rem' }}>
                    {someSkipped ? t('recurring.result.partialTitle') : t('recurring.result.successTitle')}
                </Header>
                <p style={{ color: '#555', margin: '0.25rem 0' }}>
                    {t('recurring.result.createdCount', { count: data.slotsCreated })}
                </p>
                <Progress value={data.slotsCreated} total={data.slotsPlanned}
                          color={someSkipped ? 'orange' : 'green'} size="small"
                          style={{ margin: '0.75rem auto', maxWidth: '280px' }}
                          label={`${data.slotsCreated} / ${data.slotsPlanned}`} />
                <Label color="blue" size="small">
                    <Icon name="hashtag" />
                    {t('recurring.result.seriesId')}: {data.seriesId.slice(0, 8)}…
                </Label>
            </Segment>
        </div>
    );
};

// ─── Micro-styles ─────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
    fontWeight: 'bold', fontSize: '0.95rem', marginBottom: '0.4rem',
    display: 'block', color: '#34495e',
};
const inputStyle: React.CSSProperties = {
    width: '100%', padding: '0.65rem',
    border: '1.5px solid #ced4da', borderRadius: '6px', fontSize: '0.95rem',
};
const selectStyle: React.CSSProperties = {
    ...inputStyle, appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
};
const errorTextStyle: React.CSSProperties = {
    color: '#dc3545', fontSize: '0.82rem', marginTop: '0.25rem',
};